/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-23 15:05:23
 **/

import { BaseServiceV2, ReturnData } from "@/frameworks/libraries/restservice";

class RelatedUsers extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/related_users${path ? `/${path}` : ""}`;

	public list = {
		name: "relatedUsers:list",
		execute: (
			parameter: WebServices.RelatedUsers.List.Request = {}
		): Promise<ReturnData<WebServices.RelatedUsers.List.Response>> =>
			this.get(this.list.name, this.APIPath(), parameter)
	};

	public listGroup = {
		name: "relatedUsers:listGroup",
		execute: ({
			groupId
		}: WebServices.RelatedUsers.ListGroup.Request): Promise<WebServices.RelatedUsers.ListGroup.Response> =>
			this.get(this.listGroup.name, this.APIPath(groupId))
	};
}

class User extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/users${path ? `/${path}` : ""}`;

	// @deprecated
	public checkBeforeFollow = {
		name: "checkBeforeFollow",
		execute: ({
			userId
		}: WebServices.User.CheckBeforeFollow.Request): Promise<WebServices.User.CheckBeforeFollow.Response> =>
			this.get(this.checkBeforeFollow.name, this.APIPath(`${userId}/follow_check`))
	};

	public checkBeforeFollowSpace = {
		name: "CheckBeforeFollowSpace",
		execute: ({
			userId,
			spaceId,
			...parameter
		}: WebServices.User.CheckBeforeFollowSpace.Request): Promise<WebServices.User.CheckBeforeFollowSpace.Response> =>
			this.get(
				this.checkBeforeFollowSpace.name,
				this.APIPath(`${userId}/follow_check/space/${spaceId}`),
				parameter
			)
	};

	public checkBeforeFollowConfernece = {
		name: "CheckBeforeFollowConfernece",
		execute: ({
			userId,
			conferenceId
		}: WebServices.User.CheckBeforeFollowConference.Request): Promise<WebServices.User.CheckBeforeFollowConference.Response> =>
			this.get(
				this.checkBeforeFollowConfernece.name,
				this.APIPath(`${userId}/follow_check/conference/${conferenceId}`)
			)
	};

	public checkBeforeSummon = {
		name: "checkBeforeSummon",
		execute: ({
			userId
		}: WebServices.User.CheckBeforeFollowMe.Request): Promise<WebServices.User.CheckBeforeFollowMe.Response> =>
			this.get(this.checkBeforeSummon.name, this.APIPath(`${userId}/follow_me_check`))
	};

	public entrySpace = {
		inquiry: {
			name: "entrySpace:inquiry",
			execute: (
				parameter: WebServices.User.EntrySpace.Inquiry.Request = {}
			): Promise<WebServices.User.EntrySpace.Inquiry.Response> =>
				this.get(this.entrySpace.inquiry.name, this.APIPath("entry_space"), parameter)
		},
		update: {
			name: "entrySpace:update",
			execute: (
				parameter: WebServices.User.EntrySpace.Update.Request
			): Promise<WebServices.User.EntrySpace.Update.Response> =>
				this.post(this.entrySpace.update.name, this.APIPath("entry_space"), parameter)
		}
	};

	public inquiryUsers = {
		name: "inquiryUsers",
		execute: (parameter: WebServices.User.InquiryUsers.Request): Promise<WebServices.User.InquiryUsers.Response> =>
			this.get(this.inquiryUsers.name, this.APIPath(), parameter)
	};

	public inquiryUserDetail = {
		name: "inquiryUserDetail",
		execute: ({
			userId
		}: WebServices.User.InquiryUserDetail.Request): Promise<WebServices.User.InquiryUserDetail.Response> =>
			this.get(this.inquiryUserDetail.name, this.APIPath(userId))
	};

	public me = {
		inquiry: {
			name: "me:inquiry",
			execute: (
				parameter: WebServices.User.Me.Inquiry.Request = {}
			): Promise<WebServices.User.Me.Inquiry.Response> =>
				this.get(this.me.inquiry.name, this.APIPath("me"), parameter)
		},
		update: {
			name: "me:update",
			execute: (parameter: WebServices.User.Me.Update.Request): Promise<WebServices.User.Me.Update.Response> =>
				this.post(this.me.update.name, this.APIPath(), parameter, {
					headers: { "Content-Type": "multipart/form-data" }
				})
		}
	};

	public invitesList = {
		name: "invitesList",
		execute: (
			parameter: WebServices.User.InvitesList.Request = {}
		): Promise<WebServices.User.InvitesList.Response> =>
			this.get(this.invitesList.name, this.APIPath("invites"), parameter)
	};

	public avatar = {
		current: {
			name: "avatar:current",
			execute: (
				parameter: WebServices.User.Avatar.Current.Request = {}
			): Promise<WebServices.User.Avatar.Current.Response> =>
				this.get(this.avatar.current.name, this.APIPath(`avatars/apply`), parameter)
		},
		update: {
			name: "avatar:update",
			execute: (
				parameter: WebServices.User.Avatar.Update.Request = {}
			): Promise<WebServices.User.Avatar.Update.Response> =>
				this.post(this.avatar.update.name, this.APIPath(`avatars/${parameter.id}`), parameter)
		},
		list: {
			name: "avatar:list",
			execute: (
				parameter: WebServices.User.Avatar.Get.Request = {}
			): Promise<WebServices.User.Avatar.Get.Response> =>
				this.get(this.avatar.list.name, this.APIPath("avatars"), parameter)
		},
		save: {
			name: "avatar:save",
			execute: (
				parameter: WebServices.User.Avatar.Save.Request = {}
			): Promise<WebServices.User.Avatar.Save.Response> =>
				this.post(this.avatar.save.name, this.APIPath("avatars"), parameter)
		},
		delete: {
			name: "avatar:delete",
			execute: ({
				id
			}: WebServices.User.Avatar.Delete.Request = {}): Promise<WebServices.User.Avatar.Delete.Response> =>
				this.delete(this.avatar.delete.name, this.APIPath(`avatars/${id}`))
		},
		apply: {
			name: "avatar:apply",
			execute: (
				parameter: WebServices.User.Avatar.Apply.Request = {}
			): Promise<WebServices.User.Avatar.Apply.Response> =>
				this.get(this.avatar.apply.name, this.APIPath(`avatars/apply`), parameter)
		}
	};

	public participatedSpace = {
		name: "participatedSpace",
		execute: (
			parameter: WebServices.User.GetParticipatedSpace.Request
		): Promise<WebServices.User.GetParticipatedSpace.Response> =>
			this.get(this.participatedSpace.name, this.APIPath("participated_space"), parameter)
	};

	public connect = {
		inquiry: {
			name: "connect:inquiry",
			execute: (
				parameter: WebServices.User.GetConnectedUser.Request = {}
			): Promise<WebServices.User.GetConnectedUser.Response> =>
				this.get(this.connect.inquiry.name, this.APIPath("connect"), parameter)
		},
		update: {
			name: "connect:update",
			execute: ({
				connectType,
				...parameter
			}: WebServices.User.Connect.Request): Promise<WebServices.User.Connect.Response> =>
				this.post(this.connect.update.name, this.APIPath(`connect/${connectType}`), parameter)
		},
		delete: {
			name: "connect:delete",
			execute: ({
				connectType,
				...parameter
			}: WebServices.User.DisconnectUser.Request): Promise<WebServices.User.DisconnectUser.Response> =>
				this.delete(this.connect.delete.name, this.APIPath(`connect/${connectType}`), parameter)
		}
	};

	public updateMarketingAgreement = {
		name: "update:marketing",
		execute: (
			parameter: WebServices.User.UpdateMarketingAgreement.Request
		): Promise<WebServices.User.UpdateMarketingAgreement.Response> =>
			this.post(this.updateMarketingAgreement.name, this.APIPath("marketing_agreement"), parameter)
	};

	public withdrawUser = {
		name: "withdraw",
		execute: (parameter: WebServices.User.WithdrawUser.Request): Promise<WebServices.User.WithdrawUser.Response> =>
			this.post(this.withdrawUser.name, this.APIPath("delete"), parameter)
	};
}

class UserNotification extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/users/notification_logs${path ? `/${path}` : ""}`;

	public inquiry = {
		name: "notification_logs:inquiry",
		execute: (
			parameter: WebServices.User.Notification.Inquiry.Request = {}
		): Promise<WebServices.User.Notification.Inquiry.Response> =>
			this.get(this.inquiry.name, this.APIPath(), parameter)
	};

	public read = {
		name: "notification_logs:read",
		execute: ({
			userNotificationLogId
		}: WebServices.User.Notification.Read.Request): Promise<WebServices.User.Notification.Read.Response> =>
			this.post(this.read.name, this.APIPath(`${userNotificationLogId}/read`))
	};

	public readAll = {
		name: "notification_logs:readAll",
		execute: (
			parameter: WebServices.User.Notification.ReadAll.Request = {}
		): Promise<WebServices.User.Notification.ReadAll.Response> =>
			this.post(this.readAll.name, this.APIPath("read_all"), parameter)
	};

	public readByKey = {
		name: "notification_logs:readByKey",
		execute: (
			parameter: WebServices.User.Notification.ReadByKey.Request
		): Promise<WebServices.User.Notification.ReadByKey.Response> =>
			this.post(this.readByKey.name, this.APIPath("read"), parameter)
	};
}

class UserOption extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/users/options/${path ? `${path}` : ""}`;

	public graphics = {
		inquiry: {
			name: "graphics:inquiry",
			execute: (
				parameter: WebServices.UserOption.Graphics.Inquiry.Request = {}
			): Promise<WebServices.UserOption.Graphics.Inquiry.Response> =>
				this.get(this.graphics.inquiry.name, this.APIPath("graphics"), parameter)
		},
		update: {
			name: "graphics:update",
			execute: (
				parameter: WebServices.UserOption.Graphics.Update.Request = {}
			): Promise<WebServices.UserOption.Graphics.Update.Response> =>
				this.put(this.graphics.update.name, this.APIPath("graphics"), parameter)
		}
	};

	public notification = {
		inquiry: {
			name: "notification:inquiry",
			execute: (
				parameter: WebServices.UserOption.GetNotificationOption.Request = {}
			): Promise<WebServices.UserOption.GetNotificationOption.Response> =>
				this.get(this.notification.inquiry.name, this.APIPath("notification"), parameter)
		},
		update: {
			name: "notification:update",
			execute: (
				parameter: WebServices.UserOption.GetNotificationOption.Request = {}
			): Promise<WebServices.UserOption.GetNotificationOption.Response> =>
				this.put(this.notification.update.name, this.APIPath("notification"), parameter)
		}
	};

	public groupCreateGuide = {
		update: {
			name: "group_create_guide",
			execute: (
				parameter: WebServices.UserOption.UpdateGroupCreateGuide.Request = {}
			): Promise<WebServices.UserOption.UpdateGroupCreateGuide.Response> =>
				this.put(this.groupCreateGuide.update.name, this.APIPath("group_create_guide"), parameter)
		}
	};

	public language = {
		inquiry: {
			name: "language:inquiry",
			execute: (
				parameter: WebServices.UserOption.GetUserLanguage.Request = {}
			): Promise<WebServices.UserOption.GetUserLanguage.Response> =>
				this.get(this.language.inquiry.name, this.APIPath("languages"), parameter)
		},
		update: {
			name: "language:update",
			execute: (
				parameter: WebServices.UserOption.UpdateUserLanguage.Request = {}
			): Promise<WebServices.UserOption.UpdateUserLanguage.Response> =>
				this.put(this.language.update.name, this.APIPath("languages"), parameter)
		}
	};

	public account = {
		inquiry: {
			name: "account:inquiry",
			execute: (
				parameter: WebServices.UserOption.GetAccountOption.Request = {}
			): Promise<WebServices.UserOption.GetAccountOption.Response> =>
				this.get(this.account.inquiry.name, this.APIPath("account"), parameter)
		},
		update: {
			name: "account:update",
			execute: (
				parameter: WebServices.UserOption.UpdateAccountOption.Request = {}
			): Promise<WebServices.UserOption.UpdateAccountOption.Response> =>
				this.put(this.account.update.name, this.APIPath("account"), parameter)
		}
	};
}

class UserStatus extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = "/api/users/status";
	private APIPath2 = "/api/users/default_status";

	public deleteStatus = {
		name: "userStatus:delete",
		execute: (
			parameter: WebServices.UserStatus.Delete.Request = {}
		): Promise<WebServices.UserStatus.Delete.Response> =>
			this.delete(this.deleteStatus.name, this.APIPath, parameter)
	};

	public inquiry = {
		name: "userStatus",
		execute: (
			parameter: WebServices.UserStatus.Inquiry.Request = {}
		): Promise<WebServices.UserStatus.Inquiry.Response> => this.get(this.inquiry.name, this.APIPath, parameter)
	};

	public update = {
		name: "userStatus:update",
		execute: (
			parameter: WebServices.UserStatus.Update.Request = {}
		): Promise<WebServices.UserStatus.Update.Response> => this.post(this.update.name, this.APIPath, parameter)
	};

	public defaultInquiry = {
		name: "userStatus:default",
		execute: (
			parameter: WebServices.UserStatus.DefaultInquiry.Request = {}
		): Promise<WebServices.UserStatus.DefaultInquiry.Response> =>
			this.get(this.defaultInquiry.name, this.APIPath2, parameter)
	};
}

class UserActiveStatus extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = "/api/users/active_status";

	public update = {
		name: "userActiveStatus:update",
		execute: (
			parameter: WebServices.UserActiveStatus.Update.Request
		): Promise<WebServices.UserActiveStatus.Update.Response> => this.post(this.update.name, this.APIPath, parameter)
	};

	public system_active = {
		name: "userActiveStatus:system_active",
		execute: (
			parameter: WebServices.UserActiveStatus.Active.Request
		): Promise<WebServices.UserActiveStatus.Active.Response> =>
			this.post(this.system_active.name, `${this.APIPath}/active`, parameter)
	};

	public system_away = {
		name: "userActiveStatus:system_away",
		execute: (
			parameter: WebServices.UserActiveStatus.Away.Request
		): Promise<WebServices.UserActiveStatus.Away.Response> =>
			this.post(this.system_away.name, `${this.APIPath}/away`, parameter)
	};
}

class UserSound extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = "/api/sound_notifications";

	public inquiry = {
		name: "sound:inquiry",
		execute: (
			parameter: WebServices.UserSound.GetSoundNotifications.Request = {}
		): Promise<WebServices.UserSound.GetSoundNotifications.Response> =>
			this.get(this.inquiry.name, this.APIPath, parameter)
	};
}

export default class UserService {
	constructor(
		events: API.Events,
		public readonly relatedUsers = new RelatedUsers(),
		public readonly user = new User(),
		public readonly userNotification = new UserNotification(),
		public readonly userOption = new UserOption(),
		public readonly userStatus = new UserStatus(),
		public readonly userActiveStatus = new UserActiveStatus(),
		public readonly userSound = new UserSound()
	) {
		relatedUsers.set_event(events);
		user.set_event(events);
		userNotification.set_event(events);
		userOption.set_event(events);
		userStatus.set_event(events);
		userActiveStatus.set_event(events);
		userSound.set_event(events);
	}
}
